/**
 * Agentes — regras da tela, sem React nem MUI.
 *
 * "Agente" (definição combinada com a Mariana em 24/09/2026): um trabalho que roda sozinho na
 * VPS, com regras dela, e deixa rastro. A tela mostra um card por agente (situação, última e
 * próxima rodada, números, avisos, parâmetros, interruptor, rodar agora) e, no Monitor de
 * margens, a fila de aprovação — o registro de acertos e erros que vai ensinar o agente a rodar
 * sozinho.
 */
import type { Agente, ItemMonitor, Semaforo } from './agentesService'

export const SEMAFORO: Record<Semaforo, { rotulo: string; cor: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
  verde: { rotulo: 'OK', cor: 'success' },
  amarelo: { rotulo: 'Atenção', cor: 'warning' },
  vermelho: { rotulo: 'Falhou ou parado', cor: 'error' },
  pausado: { rotulo: 'Pausado', cor: 'default' },
  rodando: { rotulo: 'Rodando agora', cor: 'info' },
}

const FUSO = 'America/Sao_Paulo'

export function quando(iso: string | null | undefined): string {
  if (!iso) return 'nunca'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

/** "há 2 h", "há 3 dias", "em 40 min" */
export function relativo(iso: string | null | undefined, agora: Date = new Date()): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const min = Math.round((d.getTime() - agora.getTime()) / 60000)
  const abs = Math.abs(min)
  const txt = abs < 60 ? `${abs} min` : abs < 48 * 60 ? `${Math.round(abs / 60)} h` : `${Math.round(abs / 1440)} dias`
  return min < 0 ? `há ${txt}` : `em ${txt}`
}

/** Linha de números da última rodada: "5 executou · 2 das quais saiu · 7 reprovou". */
export function linhaNumeros(a: Pick<Agente, 'numeros' | 'rotulos'>): string {
  return Object.entries(a.numeros || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== '—')
    .map(([k, v]) => `${typeof v === 'number' ? v.toLocaleString('pt-BR') : v} ${a.rotulos?.[k] || k}`)
    .join(' · ')
}

export const brl = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }))
export const pct = (v: number | null | undefined, d = 1) => (v == null ? '—' : `${v.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d })}%`)

export const ESFORCO: Record<NonNullable<ItemMonitor['esforco']>, { rotulo: string; cor: 'success' | 'warning' | 'error' }> = {
  verde: { rotulo: 'até 5%', cor: 'success' },
  amarelo: { rotulo: '5 a 15%', cor: 'warning' },
  vermelho: { rotulo: '15 a 30%', cor: 'error' },
  total: { rotulo: 'reprecificação total', cor: 'error' },
}

export type FiltroMonitor = { conta: string; familia: string; estado: 'pendentes' | 'aprovados' | 'reprovados' | 'todos'; regime: string }

export function filtrarMonitor(itens: ItemMonitor[], f: FiltroMonitor): ItemMonitor[] {
  return itens.filter((i) => {
    if (f.conta && i.apelido !== f.conta) return false
    if (f.familia && i.familia !== f.familia) return false
    if (f.regime && i.regime_frete !== f.regime) return false
    const d = i.decisao?.decisao
    if (f.estado === 'pendentes' && d) return false
    if (f.estado === 'aprovados' && d !== 'aprovado') return false
    if (f.estado === 'reprovados' && d !== 'reprovado') return false
    return true
  })
}

/** Resumo para o topo da fila: quantos por estado e a mediana do aumento. */
export function resumoMonitor(itens: ItemMonitor[]): { total: number; pendentes: number; aprovados: number; reprovados: number; negativas: number; cruzam: number; medianaAumento: number | null } {
  const aum = itens.map((i) => i.aumento_pct).filter((x): x is number => typeof x === 'number').sort((a, b) => a - b)
  return {
    total: itens.length,
    pendentes: itens.filter((i) => !i.decisao).length,
    aprovados: itens.filter((i) => i.decisao?.decisao === 'aprovado').length,
    reprovados: itens.filter((i) => i.decisao?.decisao === 'reprovado').length,
    negativas: itens.filter((i) => i.mc_negativa).length,
    cruzam: itens.filter((i) => i.regime_frete === 'cruza para frete grátis').length,
    medianaAumento: aum.length ? aum[Math.floor(aum.length / 2)] : null,
  }
}

/** Taxa de acerto do agente até agora: aprovados sem alteração de alvo ÷ decididos. */
export function acertoMonitor(itens: ItemMonitor[]): { decididos: number; acertos: number; taxa: number | null } {
  const dec = itens.filter((i) => i.decisao)
  const acertos = dec.filter((i) => i.decisao?.decisao === 'aprovado' && !i.decisao.alterou_alvo).length
  return { decididos: dec.length, acertos, taxa: dec.length ? (acertos / dec.length) * 100 : null }
}
